import { useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Upload, FileDown, LifeBuoy, ShoppingCart, Loader2, Check, Clock } from "lucide-react";
import ArtworkUploader from "../../components/ArtworkUploader";
import QuantityPicker from "./QuantityPicker";
import { money, deliveryEstimate } from "./boxUtils";

export default function BoxConfigurator({
  product, user, sizeKey, setSizeKey, printKey, setPrintKey, qty, setQty,
  turnaroundKey, price, calcing, uploadedFile, setUploadedFile, onAddToCart, onReset,
}) {
  const [showUpload, setShowUpload] = useState(false);
  const size = product.sizes.find((s) => s.key === sizeKey);
  const turnaround = product.turnaround_options.find((t) => t.key === turnaroundKey) || product.turnaround_options[0];

  return (
    <div className="bg-slate-50 border border-slate-200 p-5 sm:p-6" data-testid="box-configurator">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-kolt-slate">Customize and Check Prices</h2>
        <button type="button" onClick={onReset} className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-slate-500 hover:text-kolt-orange" data-testid="box-reset-btn">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </button>
      </div>

      <label className="overline block mt-6 mb-2">Print options</label>
      <div className={`grid gap-2 ${product.color_options.length === 1 ? "grid-cols-1" : "grid-cols-3"}`} data-testid="box-print-options">
        {product.color_options.map((o) => (
          <button key={o.key} type="button" onClick={() => setPrintKey(o.key)} data-testid={`print-option-${o.key}`}
            className={`text-left p-3 border transition-colors ${printKey === o.key ? "border-kolt-orange bg-white ring-1 ring-kolt-orange" : "border-slate-300 bg-white hover:border-slate-500"}`}>
            <div className="text-sm font-semibold text-kolt-slate leading-tight">{o.name}</div>
            <div className="text-[11px] font-mono text-slate-500 mt-1">{o.short}</div>
          </button>
        ))}
      </div>

      <label htmlFor="box-size" className="overline block mt-5 mb-2">Size (inside dimensions)</label>
      <select id="box-size" value={sizeKey} onChange={(e) => setSizeKey(e.target.value)} className="input-base text-base py-3" data-testid="box-size-select">
        {product.sizes.map((s) => (
          <option key={s.key} value={s.key}>{s.name}{s.note ? ` — ${s.note}` : ""}{s.usps_tier ? ` · USPS Tier ${s.usps_tier}` : ""}</option>
        ))}
      </select>
      {size && <p className="text-xs text-slate-500 mt-1.5" data-testid="box-size-fits">Fits {size.fits}.</p>}

      <label className="overline block mt-5 mb-2">Quantity</label>
      <QuantityPicker qty={qty} setQty={setQty} quoteHref={`/quote?product=${product.slug}`} />

      <div className="mt-5 flex items-end justify-between gap-4 border-t border-slate-200 pt-4" data-testid="box-price-block">
        <div className="text-sm text-slate-600">
          <div className="flex items-center gap-1.5 font-semibold text-kolt-slate"><Clock className="w-4 h-4 text-kolt-orange" />{turnaround?.business_days}</div>
          <div className="text-xs mt-0.5">after proof approval</div>
          <div className="text-xs font-mono uppercase tracking-wide mt-1" data-testid="box-delivery-estimate">Est. delivery {deliveryEstimate(turnaround)} · UPS Ground</div>
        </div>
        <div className="text-right">
          <div className="overline">Price</div>
          <div className="font-heading text-3xl font-black text-kolt-orange leading-none mt-1" data-testid="box-unit-price">
            {calcing ? <Loader2 className="w-6 h-6 animate-spin inline" /> : price ? money(price.unit_price) : "—"}
            <span className="text-base font-semibold text-kolt-slate ml-1">each</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">Subtotal: <span className="font-bold text-kolt-slate" data-testid="box-subtotal">{price ? money(price.line_total) : "—"}</span></div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-5">
        <button type="button" onClick={() => setShowUpload((v) => !v)} data-testid="box-tile-upload"
          className={`bg-white border p-4 text-center transition-colors ${showUpload || uploadedFile ? "border-kolt-orange" : "border-slate-300 hover:border-kolt-orange"}`}>
          {uploadedFile ? <Check className="w-7 h-7 mx-auto text-emerald-600" /> : <Upload className="w-7 h-7 mx-auto text-kolt-orange" />}
          <div className="text-xs font-semibold mt-2 text-kolt-slate">{uploadedFile ? "Artwork attached" : "Upload your PDF"}</div>
        </button>
        <a href={size?.templates?.outside} download data-testid="box-tile-template"
          className="bg-white border border-slate-300 hover:border-kolt-orange p-4 text-center transition-colors">
          <FileDown className="w-7 h-7 mx-auto text-kolt-orange" />
          <div className="text-xs font-semibold mt-2 text-kolt-slate">Download template</div>
        </a>
        <Link to="/contact" data-testid="box-tile-design-help"
          className="bg-white border border-slate-300 hover:border-kolt-orange p-4 text-center transition-colors">
          <LifeBuoy className="w-7 h-7 mx-auto text-kolt-orange" />
          <div className="text-xs font-semibold mt-2 text-kolt-slate">Need design help?</div>
        </Link>
      </div>

      {showUpload && (
        <div className="mt-4 bg-white border border-slate-200 p-4" data-testid="box-upload-panel">
          <ArtworkUploader user={user} productSlug={product.slug} uploadedFile={uploadedFile}
            onUploaded={(f) => setUploadedFile(f)} onCleared={() => setUploadedFile(null)} />
          <p className="text-xs text-slate-500 mt-2">No artwork yet? Add to cart anyway — you can send files after ordering. Every job gets a proof before print.</p>
        </div>
      )}

      <button type="button" onClick={onAddToCart} disabled={!price || calcing} className="btn-primary w-full mt-5" data-testid="box-add-to-cart-btn">
        <ShoppingCart className="w-4 h-4" /> Add to cart — {price ? money(price.line_total) : "…"}
      </button>
      <p className="text-[11px] text-slate-500 text-center mt-2">No payment now. Approve your proof first, then we send a secure payment link.</p>
    </div>
  );
}
